import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import { pt } from "date-fns/locale";
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Plus,
  Search,
  UsersRound,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClients, useEventCategories, useIsMobile, useUsers, clientMap } from "@/lib/hooks";
import { EVENT_TYPES, roleLabel } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { EventModal } from "@/components/EventModal";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// No telemóvel a coluna das pessoas fica só com a fotografia (fixa à esquerda)
// e cada dia ocupa exatamente metade do que sobra: vêem-se sempre dois, e os
// restantes chegam-se arrastando na horizontal.
const GRID_DESKTOP = "230px repeat(7, minmax(155px, 1fr))";
const AVATAR_COLUMN = 56;

function eventHours(event) {
  if (event.allDay || !event.startTime || !event.endTime) return 0;
  const [startHour, startMinute] = event.startTime.split(":").map(Number);
  const [endHour, endMinute] = event.endTime.split(":").map(Number);
  return Math.max(0, (endHour * 60 + endMinute - startHour * 60 - startMinute) / 60);
}

function eventTime(event) {
  if (event.allDay) return "Dia inteiro";
  if (!event.startTime) return "Sem horário";
  return event.endTime ? `${event.startTime} - ${event.endTime}` : event.startTime;
}

/** Último dia do evento: os de um só dia não têm `endDate`. */
function eventEnd(event) {
  return event.endDate || event.date;
}

function coversDay(event, date) {
  return event.date <= date && date <= eventEnd(event);
}

function spanInfo(event, date) {
  const total = Math.round((new Date(eventEnd(event)) - new Date(event.date)) / 86400000) + 1;
  if (total <= 1) return null;
  const index = Math.round((new Date(date) - new Date(event.date)) / 86400000) + 1;
  return { total, index, first: index === 1, last: index === total };
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * A semana inteira de toda a equipa num ecrã pequeno: sete colunas estreitas,
 * uma linha por pessoa, e scroll vertical quando a equipa não cabe na altura.
 */
function WeekOverviewDialog({ open, onOpenChange, days, members, eventsFor, colorOf, onPick }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] p-0 gap-0" data-testid="week-overview">
        <DialogHeader className="px-4 py-3 border-b border-border">
          <DialogTitle className="font-poppins text-base">
            {format(days[0], "d MMM", { locale: pt })} a {format(days[6], "d MMM", { locale: pt })}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto">
          <div className="grid sticky top-0 z-10 bg-card border-b border-border" style={{ gridTemplateColumns: "34px repeat(7, 1fr)" }}>
            <div />
            {days.map((day) => (
              <div key={day.toISOString()} className={`py-1.5 text-center text-[10px] font-poppins ${isSameDay(day, new Date()) ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {capitalize(format(day, "EEEEE", { locale: pt }))}
                <span className="block text-[9px]">{format(day, "d")}</span>
              </div>
            ))}
          </div>

          {members.map((member) => (
            <div key={member.id} className="grid border-b border-border/60" style={{ gridTemplateColumns: "34px repeat(7, 1fr)" }}>
              <div className="flex items-start justify-center pt-1.5" title={member.name}>
                <UserAvatar user={member} size="h-6 w-6" />
              </div>
              {days.map((day) => (
                <div key={day.toISOString()} className="min-h-[34px] p-0.5 space-y-0.5 border-l border-border/40">
                  {eventsFor(member.id, day).map((event) => (
                    <button
                      key={event.id}
                      onClick={() => onPick(event)}
                      className="block w-full h-3.5 rounded-sm"
                      style={{ backgroundColor: colorOf(event), opacity: event.status === "RASCUNHO" ? 0.6 : 1 }}
                      title={`${event.title} — ${eventTime(event)}`}
                      aria-label={`${event.title}, ${format(day, "d MMM", { locale: pt })}`}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}

          {members.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">Nada a mostrar.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamCalendar() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const scrollRef = useRef(null);
  const [dayWidth, setDayWidth] = useState(0);

  // Mede a área visível para os dias caberem certos, em vez de os fixar em `vw`
  // (que ignora as margens da página e a barra de scroll).
  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !isMobile) return undefined;
    const measure = () => setDayWidth(Math.floor((node.clientWidth - AVATAR_COLUMN) / 2));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const gridColumns = isMobile && dayWidth
    ? `${AVATAR_COLUMN}px repeat(7, ${dayWidth}px)`
    : GRID_DESKTOP;
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weekOpen, setWeekOpen] = useState(false);
  const [userFilter, setUserFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaults, setDefaults] = useState({ date: "", userId: "" });
  const [draggingId, setDraggingId] = useState(null);
  // Dia de onde o balão foi agarrado: num evento de vários dias é o que permite
  // mover o bloco inteiro mantendo o dia que ficou debaixo do rato.
  const [dragFromDate, setDragFromDate] = useState(null);
  const [dropCell, setDropCell] = useState(null);
  const { data: users = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const { data: categories = [] } = useEventCategories();
  const clientsById = useMemo(() => clientMap(clients), [clients]);
  const categoriesById = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category])),
    [categories],
  );

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );
  const rangeStart = format(days[0], "yyyy-MM-dd");
  const rangeEnd = format(days[6], "yyyy-MM-dd");

  const { data: events = [] } = useQuery({
    queryKey: ["events", rangeStart, rangeEnd],
    queryFn: async () => (
      await api.get("/events", { params: { start: rangeStart, end: rangeEnd } })
    ).data,
  });

  const normalizedSearch = search.trim().toLowerCase();
  const visibleUsers = useMemo(() => users.filter((member) => {
    if (userFilter !== "all" && member.id !== userFilter) return false;
    if (!normalizedSearch) return true;
    const userMatches = `${member.name} ${roleLabel(member)}`
      .toLowerCase()
      .includes(normalizedSearch);
    const eventMatches = events.some((event) => {
      const clientName = clientsById[event.clientId]?.name || "";
      const categoryName = categoriesById[event.categoryId]?.name || event.categoryName || "";
      return (event.assigneeIds || []).includes(member.id)
        && `${event.title} ${clientName} ${categoryName}`.toLowerCase().includes(normalizedSearch);
    });
    return userMatches || eventMatches;
  }), [users, userFilter, normalizedSearch, events, clientsById, categoriesById]);

  const colorOf = (event) =>
    categoriesById[event.categoryId]?.color || event.color || EVENT_TYPES[event.type]?.color || "#6B4F91";

  const eventsFor = (userId, day) => {
    const date = format(day, "yyyy-MM-dd");
    return events
      .filter((event) => coversDay(event, date) && (event.assigneeIds || []).includes(userId))
      .sort((first, second) => {
        if (first.allDay !== second.allDay) return first.allDay ? -1 : 1;
        return (first.startTime || "").localeCompare(second.startTime || "");
      });
  };

  const dayTotals = days.map((day) => {
    const date = format(day, "yyyy-MM-dd");
    const dayEvents = events.filter((event) => coversDay(event, date));
    return {
      events: dayEvents.length,
      hours: dayEvents.reduce((sum, event) => sum + eventHours(event), 0),
    };
  });
  const totalHours = dayTotals.reduce((sum, day) => sum + day.hours, 0);
  const activeUsers = new Set(events.flatMap((event) => event.assigneeIds || [])).size;

  const openNew = (date, userId = "") => {
    setEditing(null);
    setDefaults({ date, userId });
    setModalOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setModalOpen(true);
  };

  const canEditEvent = (event) =>
    ["ADMIN", "SUB_ADMIN"].includes(user?.role)
    || event.createdBy === user?.id
    || (event.assigneeIds || []).includes(user?.id);

  const eventsKey = ["events", rangeStart, rangeEnd];

  const moveEvent = async (eventId, dropDate, fromDate) => {
    const previous = queryClient.getQueryData(eventsKey);
    const target = (previous || []).find((event) => event.id === eventId);
    if (!target) return;
    // Arrastar a partir do meio de um bloco de vários dias desloca-o todo.
    const offset = fromDate
      ? Math.round((new Date(fromDate) - new Date(target.date)) / 86400000)
      : 0;
    const date = format(addDays(new Date(`${dropDate}T12:00:00`), -offset), "yyyy-MM-dd");
    if (target.date === date) return;
    const shift = Math.round((new Date(date) - new Date(target.date)) / 86400000);
    const endDate = target.endDate
      ? format(addDays(new Date(`${target.endDate}T12:00:00`), shift), "yyyy-MM-dd")
      : target.endDate;
    // Atualização otimista — o balão salta logo para o novo dia.
    queryClient.setQueryData(eventsKey, (old = []) =>
      old.map((event) => (event.id === eventId ? { ...event, date, endDate } : event)),
    );
    try {
      await api.patch(`/events/${eventId}/move`, { date });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (error) {
      queryClient.setQueryData(eventsKey, previous);
      toast.error(apiError(error.response?.data?.detail) || "Não foi possível mover o evento");
    }
  };

  const handleDrop = (date) => {
    const id = draggingId;
    const fromDate = dragFromDate;
    setDraggingId(null);
    setDragFromDate(null);
    setDropCell(null);
    if (id) moveEvent(id, date, fromDate);
  };

  const goToToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const shift = (direction) => setWeekStart((current) => addDays(current, direction * 7));
  const selectMyCalendar = () => setUserFilter(userFilter === user.id ? "all" : user.id);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Agenda da Equipa"
        subtitle="Organiza eventos, reuniões, ausências e trabalho da equipa numa única vista."
      />

      <Card className="overflow-hidden border-border shadow-sm">
        <div className="min-h-[68px] px-4 py-3 border-b border-border flex flex-wrap items-center gap-3 bg-card">
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-[190px] h-10 rounded-xl bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda a equipa</SelectItem>
              {users.map((member) => (
                <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center h-10 rounded-xl border border-border bg-background overflow-hidden">
            <button className="h-full px-2.5 hover:bg-muted" onClick={() => shift(-1)} aria-label="Semana anterior">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button className="h-full px-4 text-sm font-poppins font-medium hover:bg-muted whitespace-nowrap" onClick={goToToday}>
              {format(days[0], "d MMM", { locale: pt })} a {format(days[6], "d MMM", { locale: pt })}
            </button>
            <button className="h-full px-2.5 hover:bg-muted" onClick={() => shift(1)} aria-label="Semana seguinte">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {isMobile && (
            <>
              <Button variant="outline" className="h-10 rounded-xl" onClick={() => setWeekOpen(true)}>
                <CalendarRange className="h-4 w-4 mr-1.5 text-primary" /> A semana
              </Button>
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Pesquisar"
                  className="h-10 pl-9 rounded-xl"
                />
              </div>
            </>
          )}

          {/* No telemóvel a data do meio já leva a hoje; este atalho só ocuparia
              espaço na barra. */}
          <Button variant="outline" size="icon" className="h-10 w-10 rounded-full hidden md:inline-flex" onClick={goToToday} aria-label="Ir para hoje">
            <CalendarDays className="h-4 w-4 text-primary" />
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant={userFilter === user.id ? "secondary" : "outline"}
              className="rounded-full"
              onClick={selectMyCalendar}
            >
              A minha agenda
            </Button>
            <Button
              className="rounded-full bg-primary text-black hover:bg-primary/90"
              onClick={() => openNew(format(new Date(), "yyyy-MM-dd"), user.id)}
            >
              <Plus className="h-4 w-4 mr-1.5" /> Novo evento
            </Button>
          </div>
        </div>

        {/* No telemóvel o scroll encaixa de dia em dia; o `scroll-pl-14`
            corresponde à coluna fixa das fotografias, senão o dia encaixava
            por baixo dela. */}
        <div
          ref={scrollRef}
          className={`max-h-[calc(100vh-285px)] min-h-[460px] overflow-auto bg-card ${isMobile ? "snap-x snap-mandatory scroll-pl-14" : ""}`}
        >
          {/* `w-max` faz a linha ser tão larga como as colunas somadas — sem
              isso a coluna fixa das fotografias deixa de colar ao fim do
              primeiro ecrã de scroll. */}
          <div className={isMobile ? "w-max" : "min-w-[1320px]"}>
            <div className={`grid sticky top-0 z-30 ${isMobile ? "w-max" : ""}`} style={{ gridTemplateColumns: gridColumns }}>
              <div className="sticky left-0 z-40 p-2.5 border-b border-r border-border bg-card">
                {!isMobile && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Pesquisar"
                      className="h-10 pl-9 rounded-xl"
                    />
                  </div>
                )}
              </div>
              {days.map((day, index) => {
                const today = isSameDay(day, new Date());
                return (
                  <div
                    key={day.toISOString()}
                    className={`p-3 border-b border-r border-border text-center bg-card snap-start ${today ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}
                  >
                    <div className={today ? "inline-flex rounded-full bg-blue-500 text-white px-3 py-1 text-xs font-semibold" : "text-sm font-poppins font-medium"}>
                      {capitalize(format(day, "EEE d/M", { locale: pt }))}
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground flex items-center justify-center gap-2">
                      <span>{Math.round(dayTotals[index].hours * 10) / 10}h</span>
                      <span>{dayTotals[index].events} evento(s)</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleUsers.map((member) => {
              const memberEvents = events.filter((event) => (event.assigneeIds || []).includes(member.id));
              const memberHours = memberEvents.reduce((sum, event) => sum + eventHours(event), 0);
              return (
                <div key={member.id} className={`grid ${isMobile ? "w-max" : ""}`} style={{ gridTemplateColumns: gridColumns }}>
                  <div
                    className={`sticky left-0 z-20 min-h-[108px] py-3 border-b border-r border-border bg-card flex items-center gap-3 ${isMobile ? "px-0 justify-center" : "px-4"}`}
                    title={isMobile ? `${member.name} — ${roleLabel(member)}` : undefined}
                  >
                    <UserAvatar user={member} size="h-9 w-9" />
                    {!isMobile && (
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-poppins font-medium truncate">{member.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{roleLabel(member)}</div>
                        <div className="mt-1.5 flex gap-3 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock3 className="h-3 w-3" />{Math.round(memberHours * 10) / 10}h</span>
                          <span>{memberEvents.length} evento(s)</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {days.map((day) => {
                    const date = format(day, "yyyy-MM-dd");
                    const today = isSameDay(day, new Date());
                    const cellEvents = eventsFor(member.id, day);
                    const cellKey = `${member.id}::${date}`;
                    const isDropTarget = Boolean(draggingId) && dropCell === cellKey;
                    return (
                      <div
                        key={date}
                        className={`group relative min-h-[108px] p-1 border-b border-r border-border transition-colors ${isDropTarget ? "bg-primary/10 ring-2 ring-inset ring-primary/50" : `hover:bg-muted/30 ${today ? "bg-blue-50/70 dark:bg-blue-950/20" : "bg-card"}`}`}
                        onDoubleClick={() => openNew(date, member.id)}
                        onDragOver={(dragEvent) => {
                          if (!draggingId) return;
                          dragEvent.preventDefault();
                          dragEvent.dataTransfer.dropEffect = "move";
                          if (dropCell !== cellKey) setDropCell(cellKey);
                        }}
                        onDrop={(dragEvent) => {
                          dragEvent.preventDefault();
                          handleDrop(date);
                        }}
                        data-testid={`cell-${member.id}-${date}`}
                      >
                        <div className="space-y-1">
                          {cellEvents.map((event) => {
                            const color = colorOf(event);
                            const clientName = clientsById[event.clientId]?.name;
                            const cardTitle = clientName ? `${clientName} › ${event.title}` : event.title;
                            const editable = canEditEvent(event);
                            const span = spanInfo(event, date);
                            // Num bloco de vários dias só se arredondam as pontas,
                            // para se ver que os dias do meio são o mesmo evento.
                            const shape = !span
                              ? "rounded-lg"
                              : `${span.first ? "rounded-l-lg ml-0" : "rounded-l-none -ml-1"} ${span.last ? "rounded-r-lg mr-0" : "rounded-r-none -mr-1"}`;
                            return (
                              <button
                                key={event.id}
                                draggable={editable}
                                onDragStart={(dragEvent) => {
                                  dragEvent.dataTransfer.effectAllowed = "move";
                                  try { dragEvent.dataTransfer.setData("text/plain", event.id); } catch { /* ignore */ }
                                  setDraggingId(event.id);
                                  setDragFromDate(date);
                                }}
                                onDragEnd={() => {
                                  setDraggingId(null);
                                  setDragFromDate(null);
                                  setDropCell(null);
                                }}
                                onClick={() => openEdit(event)}
                                className={`relative w-full px-2 py-1.5 text-left text-white shadow-sm hover:brightness-105 transition overflow-hidden ${shape} ${editable ? "cursor-grab active:cursor-grabbing" : ""}`}
                                style={{ backgroundColor: color, opacity: draggingId === event.id ? 0.4 : (event.status === "RASCUNHO" ? 0.65 : 1) }}
                                title={span
                                  ? `${cardTitle} — dia ${span.index} de ${span.total}${editable ? ", arrasta para mover o bloco" : ""}`
                                  : (editable ? `${cardTitle} — arrasta para outro dia` : cardTitle)}
                                data-testid={`event-${event.id}`}
                              >
                                <span className="block text-[10px] leading-tight font-bold truncate">
                                  {span ? `Dia ${span.index}/${span.total} · ${eventTime(event)}` : eventTime(event)}
                                </span>
                                <span className="block text-[11px] leading-tight font-semibold truncate mt-0.5">{cardTitle}</span>
                                {(event.assigneeIds || []).length > 1 && (
                                  <UsersRound className="absolute top-1.5 right-1.5 h-3 w-3 opacity-80" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => openNew(date, member.id)}
                          className="absolute bottom-1.5 right-1.5 h-6 w-6 rounded-full border border-border bg-background text-primary shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Adicionar evento para ${member.name}`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {visibleUsers.length === 0 && (
              <div className="p-12 text-center text-sm text-muted-foreground">Nenhum membro ou evento corresponde à pesquisa.</div>
            )}
          </div>
        </div>

        <div className="min-h-[52px] border-t border-border bg-muted/30 px-4 py-2.5 flex flex-wrap items-center gap-3">
          <div className="font-poppins font-semibold text-sm mr-2">Resumo semanal</div>
          <div className="rounded-lg bg-card border border-border px-4 py-2 text-xs flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-muted-foreground" /> Horas <b>{Math.round(totalHours * 10) / 10}</b>
          </div>
          <div className="rounded-lg bg-card border border-border px-4 py-2 text-xs flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" /> Eventos <b>{events.length}</b>
          </div>
          <div className="rounded-lg bg-card border border-border px-4 py-2 text-xs flex items-center gap-2">
            <UsersRound className="h-4 w-4 text-muted-foreground" /> Pessoas <b>{activeUsers}</b>
          </div>
        </div>
      </Card>

      <WeekOverviewDialog
        open={weekOpen}
        onOpenChange={setWeekOpen}
        days={days}
        members={visibleUsers}
        eventsFor={eventsFor}
        colorOf={colorOf}
        onPick={(event) => { setWeekOpen(false); openEdit(event); }}
      />

      <EventModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        event={editing}
        defaultDate={defaults.date}
        defaultUserId={defaults.userId}
      />
    </div>
  );
}
